if (!customElements.get('notify-me-form')) {
  customElements.define(
    'notify-me-form',
    class NotifyMeForm extends HTMLElement {
      initialized = false;
      initAttempts = 0;
      pending = false;
      submittedEmail = '';

      // Children are not available in the constructor when the element is
      // re-created through innerHTML (variant change, quick view), so all
      // wiring happens here instead. See the notify-me sync in global.js
      // `renderProductInfo` for the variant-change path.
      connectedCallback() {
        if (this.initialized) return;

        this.checkboxInput = this.querySelector(`#NotifyMe-checkbox-${this.dataset.sectionId}`);
        this.form = this.querySelector('.notify-me__form') || this.querySelector('form:not(.notify-me__copy-form)');

        if (!this.checkboxInput || !this.form) {
          // The fragment is still being parsed — retry on the next frame.
          if (this.initAttempts < 5) {
            this.initAttempts += 1;
            requestAnimationFrame(() => this.connectedCallback());
          }
          return;
        }

        this.initialized = true;

        this.sectionId = this.dataset.sectionId;
        this.variantId = this.dataset.variantId;
        this.sendMerchantCopy = this.dataset.merchantCopy === 'true';

        this.liveRegion = this.querySelector(`#NotifyMe-live-region-${this.sectionId}`);
        this.copyForm = this.querySelector('.notify-me__copy-form');
        this.body = this.querySelector('.notify-me__body');
        this.emailInput = this.querySelector(`#NotifyMe-email-${this.sectionId}`);
        this.consentInput = this.querySelector(`#NotifyMe-consent-${this.sectionId}`);
        this.submitButton = this.querySelector('.notify-me__submit');
        this.successMessage = this.querySelector('.notify-me__message--success');
        this.errorMessage = this.querySelector('.notify-me__message--error');

        this.checkboxInput.disabled = false;

        this.onCheckboxChange = this.onCheckboxChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);

        this.syncFrameTargets();

        this.checkboxInput.addEventListener('change', this.onCheckboxChange);
        this.addEventListener('submit', this.onSubmit, true);

        this.onCheckboxChange();

        if (this.alreadySubscribed()) this.showSubscribed();
      }

      disconnectedCallback() {
        this.checkboxInput?.removeEventListener('change', this.onCheckboxChange);
        this.removeEventListener('submit', this.onSubmit, true);
        clearTimeout(this.copyTimeoutId);
        clearTimeout(this.finishTimeoutId);
      }

      // Quick view rewrites every section id in the markup, so the form target
      // is re-pointed at the frame actually present in this instance. A target
      // name that resolves to nothing would open the submission in a new tab.
      syncFrameTargets() {
        const frames = this.querySelectorAll('.notify-me__frame');
        if (!frames.length) return;

        this.form.target = frames[0].name;
        if (this.copyForm) this.copyForm.target = (frames[1] || frames[0]).name;
      }

      get storageKey() {
        return `notify-me:${this.variantId}`;
      }

      alreadySubscribed() {
        try {
          return localStorage.getItem(this.storageKey) === 'true';
        } catch (error) {
          return false;
        }
      }

      rememberSubscription() {
        try {
          localStorage.setItem(this.storageKey, 'true');
        } catch (error) {
          // Storage can be unavailable (private mode, blocked cookies) — the
          // subscription still went through, only the local state is lost.
        }
      }

      inputFields() {
        return [this.emailInput, this.consentInput].filter(Boolean);
      }

      onCheckboxChange() {
        const expanded = this.checkboxInput.checked;

        this.inputFields().forEach((field) => {
          field.disabled = !expanded;
        });

        if (!expanded) this.clearError();

        if (this.liveRegion && window.accessibilityStrings) {
          this.liveRegion.innerText = expanded
            ? window.accessibilityStrings.recipientFormExpanded || ''
            : window.accessibilityStrings.recipientFormCollapsed || '';
        }
      }

      validate() {
        const email = this.emailInput.value.trim();

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
          this.showError(window.forms?.emailError);
          this.emailInput.focus();
          return false;
        }

        if (this.consentInput && !this.consentInput.checked) {
          this.showError(this.dataset.consentError);
          this.consentInput.focus();
          return false;
        }

        return true;
      }

      // The submit is deliberately NOT prevented: Shopify only attaches the
      // hCaptcha token during a real submit event, and a submission without it
      // is rejected with a 400. The form targets a hidden frame, so the native
      // submit never navigates the page away.
      onSubmit(event) {
        if (event.target === this.copyForm) return;
        if (event.target !== this.form) return;

        if (this.pending) {
          event.preventDefault();
          return;
        }

        this.clearError();

        if (!this.validate()) {
          event.preventDefault();
          return;
        }

        this.submittedEmail = this.emailInput.value.trim();
        this.pending = true;
        this.setLoading(true);

        // The storefront is served with frame-ancestors 'none', so the framed
        // response never renders and there is no completion event to listen
        // for. The POST itself does reach Shopify, so the confirmation is shown
        // on a timer. The copy is delayed so Shopify's captcha can finish
        // attaching a token to the first form before the second one goes out.
        if (this.sendMerchantCopy && this.copyForm) {
          this.copyTimeoutId = setTimeout(() => this.submitCopyForm(), 600);
        }

        this.finishTimeoutId = setTimeout(() => this.finish(), 1600);
      }

      submitCopyForm() {
        const copyEmail = this.copyForm.querySelector('input[type="email"]');
        if (copyEmail) {
          copyEmail.disabled = false;
          copyEmail.value = this.submittedEmail;
        }

        if (typeof this.copyForm.requestSubmit === 'function') {
          this.copyForm.requestSubmit();
          return;
        }

        // Older browsers: form.submit() skips the submit event (and therefore
        // the captcha token), so a real button click is used instead.
        const button = document.createElement('button');
        button.type = 'submit';
        button.style.display = 'none';
        this.copyForm.appendChild(button);
        button.click();
        button.remove();
      }

      finish() {
        clearTimeout(this.copyTimeoutId);
        clearTimeout(this.finishTimeoutId);
        this.pending = false;
        this.setLoading(false);
        this.rememberSubscription();
        this.showSubscribed();
      }

      // Mirrors the theme's add-to-cart button: `.loading` on the button plus
      // an unhidden `.loading__spinner` child.
      setLoading(isLoading) {
        this.submitButton.classList.toggle('loading', isLoading);
        this.submitButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
        this.submitButton
          .querySelector('.loading__spinner')
          ?.classList.toggle('hidden', !isLoading);
      }

      showSubscribed() {
        this.classList.add('notify-me--subscribed');

        // Keep the block open and locked so the confirmation stays visible
        // when the customer comes back to this variant.
        this.checkboxInput.checked = true;
        this.checkboxInput.disabled = true;

        this.inputFields().forEach((field) => {
          field.disabled = true;
        });

        this.emailInput.value = '';
        this.body?.classList.add('hidden');
        this.successMessage?.classList.remove('hidden');
        this.clearError();
      }

      showError(message) {
        if (!this.errorMessage) return;
        const text = this.errorMessage.querySelector('span');
        if (text) text.innerText = message || this.dataset.genericError || '';
        this.errorMessage.classList.remove('hidden');
      }

      clearError() {
        this.errorMessage?.classList.add('hidden');
      }
    }
  );
}
